import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { EChartsOption } from 'echarts';
import type { ECharts } from 'echarts';
import * as L from 'leaflet';
import { RecordData } from 'src/app/types';
import { GOST_I18N } from '../../../cards/recordings/gost.i18n';
import { decodeGeohash } from '@app/shared/utils/geohash.utils';
import { RecordingEvent, SeekEvent } from '../../measured-data-details.types';
import { MeasurementEvent, ParsedMeasurement } from '../../models/parsed-measurement.model';
import { NeckOrientation } from '../../services/neck-angle.service';

import { MeasuredDataNeckAngleCardComponent } from '../neck-angle-card/measured-data-neck-angle-card.component';
import { DataFooterComponent } from '../shared/data-footer/data-footer.component';
import { DetailsHeaderComponent } from '../shared/details-header/details-header.component';
import { DisclaimerComponent } from '../shared/disclaimer/disclaimer.component';
import { EventTableComponent } from '../shared/event-table/event-table.component';
import {
  ReferenceCardComponent,
  SearchLink,
} from '../shared/reference-card/reference-card.component';
import { MeasuredDataWaveCardComponent } from '../wave-card/measured-data-wave-card.component';

@Component({
  selector: 'app-protocol-tomass',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    DetailsHeaderComponent,
    EventTableComponent,
    DataFooterComponent,
    DisclaimerComponent,
    ReferenceCardComponent,
    MeasuredDataWaveCardComponent,
    MeasuredDataNeckAngleCardComponent,
  ],
  templateUrl: './protocol-tomass.component.html',
  styleUrl: './protocol-tomass.component.scss',
})
export class ProtocolTomassComponent implements AfterViewInit, OnDestroy {
  // ── Data inputs ──
  readonly recordData = input.required<RecordData>();
  readonly parsedMeasurement = input<ParsedMeasurement | null>(null);

  // ── Wave card inputs ──
  readonly recordtype = input<number>(0);
  readonly recordLoadingSuccess = input<boolean>(true);
  readonly waveLoadingPercent = input<number | null>(null);
  readonly waveProcessing = input<boolean>(false);
  readonly waveDownloading = input<boolean>(false);
  readonly waveDownloadPercent = input<number | null>(null);
  readonly waveErrorMessage = input<string | null>(null);
  readonly selectedEvent = input<RecordingEvent | null>(null);
  readonly currentStartOffset = input<number>(0);
  readonly playing = input<boolean>(false);
  readonly timelineOffsetReference = input<number>(0);
  readonly zoomValue = input<number>(0);
  readonly showWaveCard = input<boolean>(true);

  // ── Neck angle inputs ──
  readonly chartOption = input<EChartsOption>({});
  readonly currentChartOption = input<EChartsOption>({});
  readonly currentOrientation = input<NeckOrientation | null>(null);

  // ── Wave card outputs ──
  @Output() readonly offsetToggle = new EventEmitter<boolean>();
  @Output() readonly seek = new EventEmitter<number>();
  @Output() readonly eventSeek = new EventEmitter<SeekEvent>();
  @Output() readonly playPauseClicked = new EventEmitter<void>();
  @Output() readonly zoomMinusClicked = new EventEmitter<void>();
  @Output() readonly zoomResetClicked = new EventEmitter<void>();
  @Output() readonly zoomPlusClicked = new EventEmitter<void>();

  // ── Neck angle outputs ──
  @Output() readonly chartInit = new EventEmitter<ECharts>();
  @Output() readonly currentChartInit = new EventEmitter<ECharts>();

  @Output() readonly waveHostReady = new EventEmitter<MeasuredDataWaveCardComponent>();
  private destroyed = false;
  private latestWaveCardRef?: MeasuredDataWaveCardComponent;

  @ViewChild(MeasuredDataWaveCardComponent)
  set waveCardRef(comp: MeasuredDataWaveCardComponent | undefined) {
    if (!comp || this.destroyed) return;
    this.latestWaveCardRef = comp;
    queueMicrotask(() => {
      if (this.destroyed || this.latestWaveCardRef !== comp) return;
      this.waveHostReady.emit(comp);
    });
  }

  // ── Map ──
  @ViewChild('tomassMap') tomassMapRef?: ElementRef<HTMLDivElement>;
  private map: L.Map | null = null;
  private mapRect: L.Rectangle | null = null;
  private mapMarker: L.CircleMarker | null = null;
  private viewReady = false;

  constructor() {
    effect(() => {
      // react to recordData changes for map update
      this.recordData();
      if (this.viewReady) this.updateMap();
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.updateMap();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.latestWaveCardRef = undefined;
    this.destroyMap();
  }

  // ── Local state ──
  protected readonly detailExpanded = signal(true);

  protected toggleDetail(): void {
    this.detailExpanded.update(v => !v);
    // Map needs resize after accordion opens
    if (this.detailExpanded()) {
      requestAnimationFrame(() => this.updateMap());
    }
  }

  // ── Score levels ──
  private readonly scoreLevels = ['A', 'B', 'C'];

  // ── Computed: header ──
  protected readonly patientName = computed(() =>
    this.recordData()?.data?.userName ?? '',
  );

  protected readonly measureDate = computed(() => {
    const d = this.recordData()?.data;
    const createdAt = d?.createdAt ?? d?.recordedAt ?? '';
    const offsetMatch = createdAt.match(/([+-])(\d{2}):(\d{2})$/);
    const hasNonZeroOffset =
      offsetMatch &&
      !(offsetMatch[1] === '+' && offsetMatch[2] === '00' && offsetMatch[3] === '00');

    if (hasNonZeroOffset && d?.createdAtLocationStr) {
      const dateStr = d.createdAtLocationStr.replace(/[+-]\d{2}:\d{2}$/, '').trim();
      if (!dateStr) return '';
      const h = parseInt(offsetMatch[2], 10);
      const m = parseInt(offsetMatch[3], 10);
      const tzLabel = m > 0
        ? `UTC${offsetMatch[1]}${h}:${String(m).padStart(2, '0')}`
        : `UTC${offsetMatch[1]}${h}`;
      return `${dateStr} (${tzLabel})`;
    }

    const raw = d?.createdAtLocalStr ?? d?.recordedAt ?? createdAt;
    const dateStr = raw.replace(/[+-]\d{2}:\d{2}$/, '').trim();
    if (!dateStr) return '';
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${dateStr} (${tz})` : dateStr;
    } catch {
      return dateStr;
    }
  });

  protected readonly measurer = computed(() => {
    const rd = this.recordData();
    const d = rd?.data;
    if (d?.recorderName) return $localize`:@@details.protocolTomass.measurer:計測者: ${d.recorderName}`;
    const m = rd?.meta?.measurer;
    if (m?.lastName || m?.firstName) return $localize`:@@details.protocolTomass.measurer:計測者: ${(m.lastName ?? '') + (m.firstName ?? '')}`;
    return '';
  });

  // ── Computed: Score ──
  protected readonly scoreValue = computed<string>(() => {
    const rawScore = this.recordData()?.data?.score;
    if (rawScore == null || rawScore === '') return '-';

    const normalized = String(rawScore).trim().toUpperCase();
    if (this.scoreLevels.includes(normalized)) return normalized;

    const numericScore = Number(rawScore);
    if (Number.isFinite(numericScore)) {
      const index = Math.trunc(numericScore) - 1;
      if (index >= 0 && index < this.scoreLevels.length) {
        return this.scoreLevels[index];
      }
    }
    return normalized;
  });

  protected readonly scoreLabel = computed<string>(() => {
    switch (this.scoreValue()) {
      case 'A': return $localize`:@@details.protocolTomass.scoreLabelA:良好`;
      case 'B': return $localize`:@@details.protocolTomass.scoreLabelB:中程度`;
      case 'C': return $localize`:@@details.protocolTomass.scoreLabelC:要注意`;
      default: return '-';
    }
  });

  protected readonly isGood = computed(() => this.scoreValue() === 'A');
  protected readonly isWarning = computed(() => this.scoreValue() === 'C');

  protected readonly resultLabel = computed(() =>
    this.isGood()
      ? $localize`:@@details.protocolTomass.resultLabelGood:咀嚼嚥下機能に問題なし`
      : this.isWarning()
        ? $localize`:@@details.protocolTomass.resultLabelWarning:咀嚼嚥下機能の低下を検出`
        : $localize`:@@details.protocolTomass.resultLabelNeutral:中程度`,
  );

  // ── Computed: timing ──
  protected readonly hasPst = computed(() => {
    const v = this.recordData()?.data?.pst;
    return v != null && v !== '' && Number.isFinite(Number(v));
  });

  protected readonly hasGpst = computed(() => {
    const v = this.recordData()?.data?.gpst;
    return v != null && v !== '' && Number.isFinite(Number(v));
  });

  private readonly pstNum = computed(() => {
    const v = this.recordData()?.data?.pst;
    return v != null ? Number(v) : NaN;
  });

  private readonly gpstNum = computed(() => {
    const v = this.recordData()?.data?.gpst;
    return v != null ? Number(v) : NaN;
  });

  /**
   * Adoption logic:
   * - GPST が 0 や著しく短い（嚥下未検出）→ PST を採用
   * - PST > GPST（自己申告の方が長い）→ GPST を採用（デバイス検出を信頼）
   * - それ以外 → PST を採用
   */
  protected readonly adoptedSource = computed<'gpst' | 'pst' | null>(() => {
    const pst = this.pstNum();
    const gpst = this.gpstNum();
    const gpstValid = this.hasGpst() && gpst > 1;

    if (!this.hasPst() && !gpstValid) return null;
    if (!gpstValid) return 'pst';
    if (!this.hasPst()) return 'gpst';

    // PST > GPST → デバイス検出を採用
    return pst > gpst ? 'gpst' : 'pst';
  });

  protected readonly adoptedDisplay = computed<string>(() => {
    const src = this.adoptedSource();
    if (src === 'gpst') return this.formatSeconds(this.recordData()?.data?.gpst);
    if (src === 'pst') return this.formatSeconds(this.recordData()?.data?.pst);
    return '-';
  });

  protected readonly adoptedLabel = computed<string>(() => {
    const src = this.adoptedSource();
    if (src === 'gpst') return $localize`:@@details.protocolTomass.adoptedGpst:デバイス検出`;
    if (src === 'pst') return $localize`:@@details.protocolTomass.adoptedPst:自己申告`;
    return '-';
  });

  protected readonly pstDisplay = computed<string>(() =>
    this.formatSeconds(this.recordData()?.data?.pst),
  );

  protected readonly gpstDisplay = computed<string>(() =>
    this.formatSeconds(this.recordData()?.data?.gpst),
  );

  // ── Computed: measurement detail ──
  protected readonly ageGroupDisplay = computed<string>(() => {
    const rawValue = this.recordData()?.data?.ageGroup;
    if (rawValue == null || rawValue === '') return '-';
    const trimmed = String(rawValue).trim();
    if (!trimmed) return '-';
    if (trimmed.endsWith('歳') || trimmed.endsWith('才')) return trimmed;
    return `${trimmed}${GOST_I18N.unit.yearsOld}`;
  });

  protected readonly geolocationDisplay = computed<string>(() => {
    const geohash = this.recordData()?.data?.geolocation;
    return typeof geohash === 'string' && geohash.trim().length > 0
      ? geohash.trim()
      : '-';
  });

  protected readonly hasGeolocation = computed(() =>
    this.geolocationDisplay() !== '-',
  );

  // ── Computed: events, footer ──
  protected readonly events = computed<MeasurementEvent[]>(() =>
    this.parsedMeasurement()?.events ?? [],
  );

  protected readonly dataId = computed(() => this.recordData()?.id ?? '');

  protected readonly downloadUrl = computed(() =>
    this.recordData()?.data?.storage?.csvUrl ?? '',
  );

  protected readonly searchLinks = computed<SearchLink[]>(() => {
    const links: SearchLink[] = [
      {
        label: $localize`:@@details.protocolTomass.searchLinkTomass:TOMASS（咀嚼嚥下機能チェック）`,
        url: 'https://www.google.com/search?q=TOMASS+Test+of+Masticating+and+Swallowing+Solids',
      },
      {
        label: $localize`:@@details.protocolTomass.searchLinkCriteria:TOMASS 評価基準`,
        url: 'https://www.google.com/search?q=TOMASS+咀嚼嚥下機能チェック+評価基準',
      },
      {
        label: $localize`:@@details.protocolTomass.searchLinkStats:TOMASS 咀嚼嚥下 日本 統計`,
        url: 'https://www.google.com/search?q=TOMASS+咀嚼嚥下+日本+統計',
      },
    ];
    if (this.isWarning()) {
      links.push({
        label: $localize`:@@details.protocolTomass.searchLinkRehab:咀嚼嚥下機能 リハビリテーション`,
        url: 'https://www.google.com/search?q=咀嚼嚥下機能+リハビリテーション',
      });
    }
    return links;
  });

  protected readonly disclaimer = $localize`:@@details.protocolTomass.disclaimer:TOMASSは咀嚼嚥下機能のスクリーニング検査です。判定には観察所見とデバイス計測値を含むため、最終的な臨床判断は専門医の評価と併せて行ってください。`;

  protected onEventSeek(event: SeekEvent): void {
    this.eventSeek.emit(event);
  }

  // ── Map ──
  private updateMap(): void {
    if (!this.viewReady || !this.tomassMapRef?.nativeElement || typeof window === 'undefined') return;

    const geohash = this.recordData()?.data?.geolocation?.trim();
    if (!geohash) {
      this.destroyMap();
      return;
    }

    const bounds = decodeGeohash(geohash);
    if (!bounds) {
      this.destroyMap();
      return;
    }

    if (!this.map) {
      this.map = L.map(this.tomassMapRef.nativeElement, {
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        touchZoom: false,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.map);
    }

    const latLngBounds = L.latLngBounds(
      [bounds.minLat, bounds.minLng],
      [bounds.maxLat, bounds.maxLng],
    );

    this.mapRect?.remove();
    this.mapMarker?.remove();

    this.mapRect = L.rectangle(latLngBounds, {
      color: '#2563eb',
      weight: 2,
      fillColor: '#93c5fd',
      fillOpacity: 0.2,
      dashArray: '4 4',
    }).addTo(this.map);

    this.mapMarker = L.circleMarker(latLngBounds.getCenter(), {
      radius: 4,
      color: '#ef4444',
      fillColor: '#ef4444',
      fillOpacity: 0.9,
      weight: 1,
    }).addTo(this.map);

    this.map.fitBounds(latLngBounds, { padding: [12, 12], maxZoom: 16 });
    requestAnimationFrame(() => this.map?.invalidateSize());
  }

  private destroyMap(): void {
    this.mapRect?.remove();
    this.mapRect = null;
    this.mapMarker?.remove();
    this.mapMarker = null;
    this.map?.remove();
    this.map = null;
  }

  // ── Private ──
  private formatSeconds(value: number | string | null | undefined): string {
    if (value == null || value === '') return '-';
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return `${numericValue}${GOST_I18N.unit.seconds}`;
    }
    return `${value}${GOST_I18N.unit.seconds}`;
  }

}
